"use client";

import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { usePathname } from 'next/navigation';

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

// Rotas onde o chat deve aparecer (exatamente ou como prefixo)
const ALLOWED_PATHS = ['/', '/loja', '/eventos', '/cadastro', '/carrinho', '/noticias', '/pagamento'];

type Message = {
  role: 'user' | 'model';
  parts: { text: string }[];
};

export default function Chatbot() {
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAllowed, setIsAllowed] = useState(false);
  
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Carrega histórico e estado de abertura do chat ao iniciar de forma síncrona
  useIsomorphicLayoutEffect(() => {
    let currentMessages: Message[] = [];
    
    // 1. Carrega histórico do sessionStorage
    const saved = sessionStorage.getItem('cgt_chatbot_history');
    if (saved) {
      try {
        currentMessages = JSON.parse(saved);
        setMessages(currentMessages);
      } catch (e) {
        console.error('Erro ao ler histórico do chat', e);
      }
    } 
    
    // Se não tinha histórico, adiciona a mensagem de boas-vindas
    if (currentMessages.length === 0) {
      const initialMessage: Message = { role: 'model', parts: [{ text: 'Saudações Tricolores! Como posso te ajudar hoje?' }] };
      currentMessages = [initialMessage];
      setMessages(currentMessages);
      sessionStorage.setItem('cgt_chatbot_history', JSON.stringify(currentMessages));
    }

    // 2. Só restaura o estado "Aberto" se o usuário já tiver enviado alguma mensagem (length > 1)
    const savedState = sessionStorage.getItem('cgt_chatbot_isOpen');
    if (savedState === 'true' && currentMessages.length > 1) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
      if (savedState === 'true') {
        sessionStorage.setItem('cgt_chatbot_isOpen', 'false'); // Corrige caso estivesse true mas sem mensagens
      }
    }
  }, []);

  const toggleChat = (state?: boolean) => {
    const newState = state !== undefined ? state : !isOpen;
    setIsOpen(newState);
    sessionStorage.setItem('cgt_chatbot_isOpen', String(newState));
  };

  // Verifica se a rota atual permite o chat
  useEffect(() => {
    if (!pathname) return;
    const isPathAllowed = ALLOWED_PATHS.some(path => 
      path === '/' ? pathname === '/' : pathname.startsWith(path)
    );
    setIsAllowed(isPathAllowed);
  }, [pathname]);

  const prevIsOpen = useRef(isOpen);

  // Rola para a última mensagem de forma síncrona antes do navegador desenhar a tela
  useIsomorphicLayoutEffect(() => {
    if (isOpen) {
      const justOpened = !prevIsOpen.current;
      
      if (justOpened) {
        // No flex-col-reverse, scrollTop = 0 é o fundo absoluto (ignora padding visual)
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = 0;
        }
      }

      // Aplica o timeout apenas para ajustar após a transição ou animações suaves de novas mensagens
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTo({ 
            top: 0,
            behavior: justOpened ? 'auto' : 'smooth'
          });
        }
      }, 150);
    }
    prevIsOpen.current = isOpen;
  }, [messages, isOpen]);

  const handleSend = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage: Message = { role: 'user', parts: [{ text: input.trim() }] };
    const newMessages = [...messages, userMessage];
    
    setMessages(newMessages);
    setInput('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'; // Reseta a altura após enviar
    }
    setIsLoading(true);
    
    // Salva no sessionStorage para persistir
    sessionStorage.setItem('cgt_chatbot_history', JSON.stringify(newMessages));

    // Prepara as mensagens para a API injetando o contexto da página atual (invisível para o usuário)
    const apiMessages = [...newMessages];
    
    // Lê o conteúdo da página para o bot entender o que o usuário está vendo
    let pageContent = '';
    if (typeof document !== 'undefined') {
      try {
        const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
          acceptNode: (node) => {
            let parent = node.parentElement;
            while (parent) {
              if (parent.id === 'cgt-chatbot-container' || parent.tagName === 'SCRIPT' || parent.tagName === 'STYLE') {
                return NodeFilter.FILTER_REJECT;
              }
              parent = parent.parentElement;
            }
            return NodeFilter.FILTER_ACCEPT;
          }
        });
        let currentNode = walker.nextNode();
        while (currentNode) {
          if (currentNode.nodeValue && currentNode.nodeValue.trim()) {
            pageContent += currentNode.nodeValue.trim() + ' ';
          }
          currentNode = walker.nextNode();
        }
        pageContent = pageContent.substring(0, 4000);
      } catch (e) {
        console.error('Erro ao ler contexto da página', e);
      }
    }

    const contextoPagina = `[Contexto do Sistema: O usuário está na página "${pathname}". O conteúdo visível da página neste momento é: \n"""\n${pageContent}\n"""\n\nAnalise esse conteúdo para explicar o que ele deve fazer, detalhar os planos, campos de formulário, eventos ou produtos que estejam descritos no texto acima. Ajude-o ativamente com base nesse contexto!]\n\n`;
    
    apiMessages[apiMessages.length - 1] = {
      role: 'user',
      parts: [{ text: contextoPagina + input.trim() }]
    };

    try {
      const res = await fetch('https://api.arfainfo.com.br/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: apiMessages }),
      });

      const data = await res.json();
      
      if (res.ok && data.sucesso) {
        const aiMessage: Message = { role: 'model', parts: [{ text: data.resposta }] };
        const finalMessages = [...newMessages, aiMessage];
        setMessages(finalMessages);
        sessionStorage.setItem('cgt_chatbot_history', JSON.stringify(finalMessages));
      } else {
        const errorMessage: Message = { role: 'model', parts: [{ text: 'Ops! Ocorreu um erro no sistema. Tente novamente mais tarde.' }] };
        setMessages([...newMessages, errorMessage]);
      }
    } catch (error) {
      console.error(error);
      const errorMessage: Message = { role: 'model', parts: [{ text: 'Ops! Erro de conexão. Tente novamente mais tarde.' }] };
      setMessages([...newMessages, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Se a página não for permitida, o componente fica invisível,
  // MAS continua renderizado na árvore do layout para preservar seu estado no React.
  if (!isAllowed) return null;

  return (
    <div id="cgt-chatbot-container" className="fixed bottom-6 right-6 z-[999] font-sans">
      {/* Container do Chat */}
      <div 
        className={`absolute bottom-16 right-0 w-80 sm:w-96 bg-white/95 backdrop-blur-md border border-gray-200 shadow-2xl rounded-2xl flex flex-col overflow-hidden overscroll-none transition-all duration-300 transform origin-bottom-right ${isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0 pointer-events-none'}`}
        style={{ height: '500px', maxHeight: 'calc(100vh - 120px)' }}
      >
        {/* Header */}
        <div className="bg-[#6b1122] text-white p-4 flex justify-between items-center shadow-md z-10 relative">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center p-1 shadow-inner">
              <img src="https://casadoguerreirotricolor.com.br/logo_pre.png" alt="Logo" className="w-full h-full object-contain" />
            </div>
            <div>
              <h3 className="font-bold text-sm leading-tight font-['Bebas_Neue'] text-lg tracking-wider">ASSISTENTE TRICOLOR</h3>
              <p className="text-[10px] text-green-400 font-bold tracking-widest uppercase">Online agora</p>
            </div>
          </div>
          <button onClick={() => toggleChat(false)} className="text-white/80 hover:text-white transition-colors p-1">
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Messages */}
        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto overscroll-none p-4 flex flex-col-reverse bg-gray-50/50 gap-4">
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-white border border-gray-100 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm flex gap-1">
                <div className="w-2 h-2 bg-[#6b1122] rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-[#6b1122] rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2 h-2 bg-[#6b1122] rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
              </div>
            </div>
          )}
          {[...messages].reverse().map((msg, i) => (
            <div 
              key={i} 
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div 
                className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${
                  msg.role === 'user' 
                    ? 'bg-[#006437] text-white rounded-br-sm' 
                    : 'bg-white text-gray-800 border border-gray-100 rounded-bl-sm'
                }`}
                dangerouslySetInnerHTML={{ 
                  __html: msg.parts[0].text
                    .replace(/\*\*([\s\S]*?)\*\*/g, '<strong>$1</strong>')
                    .replace(/\[([\s\S]*?)\]\(([\s\S]*?)\)/g, '<a href="$2" class="underline text-[#006437] font-bold hover:text-[#6b1122] transition-colors" target="_blank" rel="noopener noreferrer">$1</a>')
                    .replace(/\n/g, '<br/>') 
                }}
              />
            </div>
          ))}
        </div>

        {/* Input */}
        <form onSubmit={handleSend} className="p-3 bg-white border-t border-gray-100 flex items-end gap-2">
          <textarea
            ref={textareaRef}
            rows={1}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Digite sua mensagem..."
            disabled={isLoading}
            className="flex-1 bg-gray-100 text-gray-800 placeholder-gray-500 border-transparent focus:bg-white focus:border-[#6b1122] focus:ring-1 focus:ring-[#6b1122] rounded-2xl px-4 py-2.5 text-sm outline-none transition-all disabled:opacity-50 resize-none overflow-y-auto"
            style={{ minHeight: '40px', maxHeight: '120px' }}
          />
          <button 
            type="submit" 
            disabled={isLoading || !input.trim()}
            className="bg-[#6b1122] text-white w-10 h-10 rounded-full flex items-center justify-center hover:bg-[#4a0916] transition-colors disabled:opacity-50 shadow-md"
          >
            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" className="rotate-90"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
          </button>
        </form>
      </div>

      {/* Floating Button */}
      <button 
        onClick={() => toggleChat()}
        className={`text-white w-14 h-14 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 transform hover:scale-110 z-50 relative ${isOpen ? 'bg-gray-800 rotate-90' : 'bg-[#6b1122] rotate-0'}`}
      >
        {isOpen ? (
          <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
        ) : (
          <svg width="28" height="28" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>
        )}
      </button>
    </div>
  );
}
